/**
 * JSON:API Utilities
 *
 * Provides utilities for parsing and working with JSON:API formatted responses
 *
 * BREAKING CHANGES in v2.0:
 *
 * 1. ✅ Unified Data Structure (v2.0):
 *    ALL relationships are now nested under "relationships" key, whether resolved or not.
 *
 *    Before: { id: '1', name: 'example', company: { type: 'company', id: '5' } }
 *    After:  { id: '1', name: 'example', relationships: { company: { type: 'company', id: '5' } } }
 *
 *    MIGRATION: Update all consumers to access via `data.relationships.X` instead of `data.X`
 *
 * 2. Performance: resolveRelationship() re-normalizes on every call
 *    WORKAROUND: Use bulkResolveRelationships() for multiple resolutions
 *
 * @module shared/utils/jsonapi
 */

/**
 * @typedef {Object} JsonApiResource
 * @property {string} id - Resource ID
 * @property {string} type - Resource type
 * @property {Object} [attributes] - Resource attributes
 * @property {Object} [relationships] - Resource relationships
 */

/**
 * @typedef {Object} JsonApiRelationship
 * @property {JsonApiResourceIdentifier|JsonApiResourceIdentifier[]|null} [data] - Relationship data
 */

/**
 * @typedef {Object} JsonApiResourceIdentifier
 * @property {string} id - Resource ID
 * @property {string} type - Resource type
 */

/**
 * @typedef {Object} JsonApiResponse
 * @property {JsonApiResource|JsonApiResource[]} data - Response data
 * @property {JsonApiResource[]} [included] - Included resources
 * @property {Object} [meta] - Response metadata
 * @property {Object} [links] - Pagination links
 */

/**
 * @typedef {Object} JsonApiError
 * @property {string} title - Error title
 * @property {string} detail - Error detail message
 * @property {Object} source - Error source
 * @property {string} source.pointer - JSON pointer to error location (e.g., "/name", "/websites/0/domain")
 * @property {string} [type] - Error type ("data" or "type")
 * @property {Object} [meta] - Additional error metadata
 * @property {string} [meta.request_identifier] - Request identifier for debugging
 */

/**
 * @typedef {Object.<string, Object.<string, JsonApiResource>>} NormalizedIncluded
 * Map of type -> id -> resource for fast lookups
 */

/**
 * Normalizes included resources by creating a lookup map by type and id
 *
 * @param {JsonApiResource[]} [included=[]] - The included array from JSON:API response
 * @returns {NormalizedIncluded} Map of type -> id -> resource
 *
 * @example
 * const normalized = normalizeIncluded(response.included);
 * const user = normalized['user']?.[userId];
 */
export function normalizeIncluded(included = []) {
    const normalized = {};

    included.forEach((resource) => {
        if (!normalized[resource.type]) {
            normalized[resource.type] = {};
        }
        normalized[resource.type][resource.id] = resource;
    });

    return normalized;
}

/**
 * Resolves a relationship reference to its full resource from included data
 * WARNING: This function re-normalizes the included array on every call.
 * For bulk operations, use bulkResolveRelationships() instead.
 *
 * @param {JsonApiRelationship|null} relationship - Relationship data from JSON:API
 * @param {JsonApiResource[]} [included=[]] - The included array from JSON:API response
 * @returns {Object|Object[]|null} The resolved resource(s) or null if not found
 *
 * @example
 * // To-one relationship
 * const company = resolveRelationship(contact.relationships.company, included);
 *
 * @example
 * // To-many relationship
 * const mailAccounts = resolveRelationship(mailDomain.relationships.mailAccounts, included);
 */
export function resolveRelationship(relationship, included = []) {
    if (!relationship) {
        return null;
    }

    const normalized = normalizeIncluded(included);

    // Handle to-many relationships (array)
    if (Array.isArray(relationship.data)) {
        return relationship.data
            .map((ref) => {
                return normalized[ref.type]?.[ref.id] || null;
            })
            .filter(Boolean);
    }

    // Handle to-one relationships (object)
    if (relationship.data) {
        return normalized[relationship.data.type]?.[relationship.data.id] || null;
    }

    return null;
}

/**
 * Efficiently resolves multiple relationships from resources by normalizing included array once
 * Use this in stores when resolving relationships for arrays of resources
 *
 * @param {JsonApiResource[]} resources - Array of resources with relationships to resolve
 * @param {string[]} relationshipKeys - Array of relationship names to resolve
 * @param {JsonApiResource[]} [included=[]] - The included array from JSON:API response
 * @returns {Object[]} Array of objects with resolved relationships
 *
 * @example
 * // In a store (like ssl.store.js)
 * const certificates = bulkResolveRelationships(
 *   data.data,
 *   ['product', 'sites'],
 *   data.included
 * );
 * // Returns: [{ product: {...}, sites: [{...}] }, ...]
 */
export function bulkResolveRelationships(resources, relationshipKeys, included = []) {
    if (!Array.isArray(resources) || resources.length === 0) {
        return [];
    }

    if (!Array.isArray(relationshipKeys) || relationshipKeys.length === 0) {
        return resources;
    }

    // Normalize included array ONCE for all resources
    const normalized = normalizeIncluded(included);

    return resources.map((resource) => {
        if (!resource.relationships) {
            return resource;
        }

        const resolved = {};

        relationshipKeys.forEach((key) => {
            const relationship = resource.relationships[key];

            if (relationship) {
                const resolvedData = resolveRelationshipFromNormalized(relationship, normalized);

                if (resolvedData !== null) {
                    resolved[key] = resolvedData;
                } else if (import.meta.env.PROD && relationship.data) {
                    console.warn(
                        `[jsonapi] Relationship "${key}" not found in included array.`,
                        `Resource: ${resource.type}/${resource.id}`
                    );
                }
            }
        });

        return resolved;
    });
}

/**
 * Resolves a relationship reference using pre-normalized included data
 * More efficient than resolveRelationship() when processing multiple relationships
 *
 * @param {JsonApiRelationship|null} relationship - Relationship data from JSON:API
 * @param {NormalizedIncluded} normalizedIncluded - Pre-normalized included lookup map
 * @returns {JsonApiResource|JsonApiResource[]|null} The resolved resource(s) or null
 * @private
 */
function resolveRelationshipFromNormalized(relationship, normalizedIncluded) {
    if (!relationship || !normalizedIncluded) {
        return null;
    }

    // Handle to-many relationships (array)
    if (Array.isArray(relationship.data)) {
        return relationship.data
            .map((ref) => {
                return normalizedIncluded[ref.type]?.[ref.id] || null;
            })
            .filter(Boolean);
    }

    // Handle to-one relationships (object)
    if (relationship.data) {
        return normalizedIncluded[relationship.data.type]?.[relationship.data.id] || null;
    }

    return null;
}

/**
 * Extracts resources of a specific type from included array
 *
 * @param {JsonApiResource[]} [included=[]] - The included array from JSON:API response
 * @param {string} type - The resource type to extract
 * @returns {JsonApiResource[]} Array of resources matching the type
 *
 * @example
 * const mailAccounts = extractIncludedType(response.included, 'mailAccount');
 */
export function extractIncludedType(included = [], type) {
    return included.filter((resource) => resource.type === type);
}

/**
 * Finds a single resource by type and id from included array
 *
 * @param {JsonApiResource[]} [included=[]] - The included array from JSON:API response
 * @param {string} type - The resource type
 * @param {string} id - The resource id
 * @returns {JsonApiResource|null} The resource or null if not found
 *
 * @example
 * const tldReq = findIncludedResource(included, 'tldRequirement', requirementId);
 */
export function findIncludedResource(included = [], type, id) {
    return included.find((resource) => resource.type === type && resource.id === id) || null;
}

/**
 * Parse and unwrap API responses with optional relationship resolution
 *
 * Relationships are ALWAYS nested under "relationships" key, whether resolved or not.
 * - Unresolved: { id: '1', ...attributes, relationships: { user: { type: 'user', id: '2' } } }
 * - Resolved:   { id: '1', ...attributes, relationships: { user: { id: '2', name: 'John' } } }
 *
 * @param {JsonApiResponse} payload - JSON:API response payload
 * @param {string[]} [resolveRelationships=null] - Optional array of relationship names to resolve
 * @returns {Object|Object[]} Flattened resource(s) with relationships always nested
 *
 * @example
 * // Without resolution (relationships as references)
 * const domain = parseResponse(payload);
 * // Returns: { id: '1', name: 'example.com', relationships: { registrantContact: { type: 'contact', id: '5' } } }
 *
 * @example
 * // With resolution (relationships as full objects)
 * const product = parseResponse(payload, ['prices']);
 * // Returns: { id: '1', name: 'Hosting', relationships: { prices: [{id: '1', ...}] } }
 */
export function parseResponse(payload, resolveRelationships = null) {
    if (!payload || typeof payload !== 'object') {
        return payload;
    }

    const { data, included } = payload;

    // Pre-normalize included array once if we're resolving relationships
    const normalizedIncluded = resolveRelationships?.length > 0 && included ? normalizeIncluded(included) : null;

    // Handle list
    if (Array.isArray(data)) {
        return data.map((item) => flattenResource(item, resolveRelationships, normalizedIncluded));
    }

    // Handle single resource
    return flattenResource(data, resolveRelationships, normalizedIncluded);
}

/**
 * Parse and unwrap API responses with pagination metadata preserved
 *
 * @param {JsonApiResponse} payload - JSON:API response payload
 * @param {string[]} [resolveRelationships=null] - Optional array of relationship names to resolve
 * @returns {{data: Object|Object[], meta: Object, links: Object}} Parsed data with pagination metadata
 *
 * @example
 * const response = await api.get('/activities');
 * const { data, meta, links } = parseResponseWithMeta(response.data, ['user']);
 */
export function parseResponseWithMeta(payload, resolveRelationships = null) {
    return {
        data: parseResponse(payload, resolveRelationships),
        meta: payload.meta,
        links: payload.links,
    };
}

/**
 * Extract response with included resources as a lookup map
 * Returns flattened data and a map of included resources by "type:id" key
 *
 * @param {JsonApiResponse} payload - JSON:API response payload
 * @returns {{data: Object|Object[], included: Object.<string, Object>}} Data and included lookup map
 *
 * @example
 * const { data, included } = extractIncluded(payload);
 * const user = included['user:123'];
 */
export function extractIncluded(payload) {
    if (!payload || typeof payload !== 'object') {
        return { data: payload, included: {} };
    }

    // Create a map of included resources by type:id
    const includedMap = {};
    if (Array.isArray(payload.included)) {
        payload.included.forEach((resource) => {
            const key = `${resource.type}:${resource.id}`;
            includedMap[key] = flattenResource(resource);
        });
    }

    // Flatten the main data
    const data = Array.isArray(payload.data)
        ? payload.data.map((item) => flattenResource(item))
        : flattenResource(payload.data);

    return { data, included: includedMap };
}

/**
 * Maps JSON:API errors to field-keyed object for form validation
 * Transforms error pointer format ("/field/name") to dot notation ("field.name")
 *
 * @param {JsonApiError[]} errors - Array of JSON:API errors
 * @param {boolean} [includeTypeErrors=false] - Whether to include "type" errors (vs only "data" errors)
 * @returns {Object.<string, string>} Map of field path to error message
 *
 * @example
 * // Basic usage (matches useForm.js pattern)
 * const errors = [
 *   { detail: "Name is required", source: { pointer: "/name" } },
 *   { detail: "Invalid domain", source: { pointer: "/websites/0/domain" } }
 * ];
 * const fieldErrors = mapErrorsToFields(errors);
 * // Returns: { "name": "Name is required", "websites.0.domain": "Invalid domain" }
 *
 * @example
 * // With type filtering
 * const dataErrors = mapErrorsToFields(errors.filter(e => e.type === 'data'));
 */
export function mapErrorsToFields(errors, includeTypeErrors = false) {
    if (!Array.isArray(errors)) {
        return {};
    }

    return errors.reduce((carry, error) => {
        // Skip type errors unless explicitly included
        if (!includeTypeErrors && error.type === 'type') {
            return carry;
        }

        if (!error.source?.pointer) {
            return carry;
        }

        // Replace leading slash with empty string, remaining slashes with dots
        const field = error.source.pointer.replace(/^\//, '').replace(/\//g, '.');

        carry[field] = error.detail || '';

        return carry;
    }, {});
}

/**
 * Validates if a payload is a valid JSON:API response structure
 * Checks for presence of required "data" property and valid structure
 *
 * @param {*} payload - Payload to validate
 * @returns {boolean} True if valid JSON:API response
 *
 * @example
 * if (isValidJsonApiResponse(response.data)) {
 *   const parsed = parseResponse(response.data);
 * }
 */
export function isValidJsonApiResponse(payload) {
    if (!payload || typeof payload !== 'object') {
        return false;
    }

    // Must have a data property
    if (!('data' in payload)) {
        return false;
    }

    const { data } = payload;

    // data can be null, array, or object
    if (data === null) {
        return true;
    }

    if (Array.isArray(data)) {
        // All items must be valid resources
        return data.every((item) => isValidJsonApiResource(item));
    }

    // Single resource
    return isValidJsonApiResource(data);
}

/**
 * Validates if an object is a valid JSON:API resource
 *
 * @param {*} resource - Resource to validate
 * @returns {boolean} True if valid JSON:API resource
 * @private
 */
function isValidJsonApiResource(resource) {
    if (!resource || typeof resource !== 'object') {
        return false;
    }

    // Must have id and type
    return typeof resource.id === 'string' && typeof resource.type === 'string';
}

/**
 * Flatten resource object with optional relationship resolution
 *
 * @param {JsonApiResource} resource - JSON:API resource object
 * @param {string[]} [resolveRelationships=null] - Optional array of relationship names to resolve
 * @param {NormalizedIncluded} [normalizedIncluded=null] - Pre-normalized included lookup map
 * @returns {Object} Flattened resource
 * @private
 */
function flattenResource(resource, resolveRelationships = null, normalizedIncluded = null) {
    if (!resource) {
        return resource;
    }

    const { id, attributes = {} } = resource;

    // Always build relationships object
    const relationships = {};

    if (resource.relationships) {
        Object.entries(resource.relationships).forEach(([key, rel]) => {
            if (!rel.data) {
                // Relationship exists but has no data
                return;
            }

            // Check if this relationship should be resolved
            if (resolveRelationships?.includes(key) && normalizedIncluded) {
                // Resolve the relationship
                const resolved = resolveRelationshipFromNormalized(rel, normalizedIncluded);

                if (resolved !== null) {
                    // Flatten resolved resources recursively
                    if (Array.isArray(resolved)) {
                        relationships[key] = resolved.map((r) => flattenResource(r));
                    } else {
                        relationships[key] = flattenResource(resolved);
                    }
                } else if (import.meta.env.DEV) {
                    // Development warning for missing relationship data
                    console.warn(
                        `[parseResponse] Relationship "${key}" was requested but not found in included array.`,
                        `Resource type: "${resource.type}", id: "${resource.id}"`
                    );
                    // Keep as reference if resolution failed
                    relationships[key] = rel.data;
                } else if (import.meta.env.PROD) {
                    // Production: log warning for missing relationship data
                    console.warn(
                        `[jsonapi] Relationship "${key}" not found in included array.`,
                        `Resource: ${resource.type}/${resource.id}`
                    );
                    // Keep as reference if resolution failed
                    relationships[key] = rel.data;
                }
            } else {
                // Keep as reference (unresolved)
                relationships[key] = rel.data;
            }
        });
    }

    // Always nest relationships
    return {
        id,
        ...attributes,
        relationships,
    };
}
